import { useState, useMemo } from 'react'
import { Link } from '@inertiajs/react'
import { motion, AnimatePresence } from 'framer-motion'
import SeoHead from '@/components/SeoHead'
import AnimatedNumber from '@/components/AnimatedNumber'
import MarketingFooter from '@/components/MarketingFooter'
import { calculateInterestOnly } from '@/lib/calculations'

// ─── Fade-in animation preset ────────────────────────────────────────────────

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
}

// ─── Inline SVG icons ────────────────────────────────────────────────────────

function DocumentIcon() {
  return (
    <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  )
}

function ChartIcon() {
  return (
    <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}

function FormIcon() {
  return (
    <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
    </svg>
  )
}

function UploadIcon() {
  return (
    <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
    </svg>
  )
}

// ─── FAQ data ────────────────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    question: 'Do I need to issue 1098s for private loans?',
    answer:
      'If any single borrower paid you $600 or more in interest during the tax year, you are generally required to file Form 1098. LendSolo automatically flags qualifying borrowers so you don\u2019t have to track the threshold manually. Always consult your CPA for advice specific to your situation.',
  },
  {
    question: 'Can I import my existing loan data?',
    answer:
      'Yes. LendSolo supports CSV and Excel imports with automatic column detection. Most users complete their import in under 2 minutes.',
  },
  {
    question: 'What if my loans are interest-only or balloon?',
    answer:
      'All three loan structures \u2014 standard amortizing, interest-only, and balloon \u2014 are fully supported with automatic payment calculations.',
  },
  {
    question: 'How much does it cost?',
    answer:
      'Free for up to 2 loans with full features. Solo plan is $29/month for up to 5 loans. Pro plan is $49/month for up to 25 loans and includes 1098 generation.',
  },
]

// ─── Feature cards data ──────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: <DocumentIcon />,
    title: 'Automatic Interest Tracking',
    description:
      'Every payment auto-splits into principal and interest. No formulas, no manual allocation, no mistakes.',
  },
  {
    icon: <ChartIcon />,
    title: 'Year-End Tax Reports',
    description:
      'One-click annual interest summary by borrower. Export as PDF, CSV, or QuickBooks QBO file.',
  },
  {
    icon: <FormIcon />,
    title: '1098 Generation',
    description:
      'LendSolo auto-generates Form 1098 for any borrower who paid you $600+ in interest. Download or email directly.',
  },
  {
    icon: <UploadIcon />,
    title: 'Spreadsheet Import',
    description:
      'Upload your existing CSV or Excel file. Auto-map columns, review, and migrate in under 2 minutes.',
  },
]

// ─── SEO schema ──────────────────────────────────────────────────────────────

const seoSchema = {
  '@graph': [
    {
      '@type': 'FinancialProduct',
      name: 'LendSolo Tax Tracking for Private Loans',
      description:
        'Stop scrambling at tax time. LendSolo tracks interest income across all your private loans and generates 1098 reports automatically.',
      url: 'https://lendsolo.com/private-loan-tax-tracking',
      provider: { '@type': 'Organization', name: 'LendSolo' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ],
}

// ─── Page Component ──────────────────────────────────────────────────────────

export default function TaxTracking() {
  return (
    <>
      <SeoHead
        title="Track Private Loans for Taxes — 1098 Made Easy | LendSolo"
        description="Stop scrambling at tax time. LendSolo tracks interest income across all your private loans and generates 1098 reports automatically. Free to start."
        canonicalPath="/private-loan-tax-tracking"
        ogType="website"
        schema={seoSchema}
      />

      <div className="min-h-screen bg-white">
        <Header />
        <HeroSection />
        <ProblemNarrative />
        <CalculatorSection />
        <SolutionFeatures />
        <SocialProofStats />
        <RelatedPages />
        <FaqSection />
        <FinalCta />
        <MarketingFooter />
      </div>
    </>
  )
}

// ─── Header ──────────────────────────────────────────────────────────────────

function Header() {
  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#0F1419' }}
          >
            <span className="text-emerald-400 font-bold text-sm">LS</span>
          </div>
          <span className="text-lg font-bold text-gray-900">LendSolo</span>
        </Link>

        <nav className="flex items-center gap-3">
          <Link
            href="/blog"
            className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
          >
            Blog
          </Link>
          <Link
            href="/calculators"
            className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
          >
            Calculators
          </Link>
          <Link
            href="/users/sign_in"
            className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2"
          >
            Sign In
          </Link>
          <Link
            href="/users/sign_up"
            className="text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg transition-colors"
          >
            Get Started
          </Link>
        </nav>
      </div>
    </header>
  )
}

// ─── Hero Section ────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section
      style={{ background: 'linear-gradient(165deg, #0E4D30 0%, #081C12 100%)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            Your Spreadsheet Can&rsquo;t Generate a 1098.{' '}
            <span style={{ color: '#34D399' }}>LendSolo Can.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-300 font-body leading-relaxed max-w-2xl">
            Track interest income across every private loan. Generate tax-ready reports in
            seconds &mdash; not hours of formula auditing. Free to start, no credit card
            required.
          </p>
          <Link
            href="/users/sign_up"
            className="mt-8 inline-flex items-center px-8 py-3.5 rounded-lg text-base font-semibold text-gray-900 transition-colors hover:brightness-110"
            style={{ backgroundColor: '#34D399' }}
          >
            Start Free &rarr;
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

// ─── Problem Narrative ───────────────────────────────────────────────────────

function ProblemNarrative() {
  return (
    <section className="bg-white py-16 sm:py-24">
      <motion.div {...fadeInUp} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="font-body text-lg sm:text-xl text-gray-700 leading-loose">
          It&rsquo;s January, your CPA needs numbers, and your spreadsheet is a mess.
          You&rsquo;ve got multiple loans with different structures and you need to know
          exactly how much interest each borrower paid you last year. The IRS requires you
          to report interest income &mdash; and if you&rsquo;re receiving $600+ from any
          borrower, you need to issue a 1098. Try doing that from a spreadsheet.
        </p>
      </motion.div>
    </section>
  )
}

// ─── Inline Interest-Only Calculator ─────────────────────────────────────────

function CalculatorSection() {
  const [principal, setPrincipal] = useState(100000)
  const [rate, setRate] = useState(10)
  const [termMonths, setTermMonths] = useState(12)

  const result = useMemo(() => {
    return calculateInterestOnly(principal, rate, termMonths, new Date())
  }, [principal, rate, termMonths])

  return (
    <section className="bg-gray-50 py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp}>
          <p className="text-sm font-semibold tracking-wide uppercase text-emerald-600 font-body">
            Interest-Only Calculator
          </p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-gray-900">
            Try it now &mdash; no signup needed
          </h2>

          <div className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-body mb-1.5">
                  Loan Amount ($)
                </label>
                <input
                  type="number"
                  value={principal}
                  onChange={(e) => setPrincipal(Number(e.target.value))}
                  min={0}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-body mb-1.5">
                  Annual Rate (%)
                </label>
                <input
                  type="number"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  min={0}
                  step={0.25}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-body mb-1.5">
                  Term (months)
                </label>
                <input
                  type="number"
                  value={termMonths}
                  onChange={(e) => setTermMonths(Number(e.target.value))}
                  min={1}
                  max={360}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-gray-100">
              <div>
                <p className="text-sm text-gray-500 font-body">Monthly Interest</p>
                <AnimatedNumber
                  value={result.monthlyInterestPayment}
                  prefix="$"
                  className="text-2xl font-bold text-gray-900 font-display"
                />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-body">Total Interest</p>
                <AnimatedNumber
                  value={result.totalInterest}
                  prefix="$"
                  className="text-2xl font-bold text-gray-900 font-display"
                />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-body">Balloon Payment</p>
                <AnimatedNumber
                  value={result.balloonPayment}
                  prefix="$"
                  className="text-2xl font-bold text-gray-900 font-display"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              href="/users/sign_up"
              className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-semibold font-body transition-colors"
            >
              Ready to track this for real? &rarr; Start Free
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ─── Solution Features ───────────────────────────────────────────────────────

function SolutionFeatures() {
  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="rounded-2xl border border-gray-200 p-6 sm:p-8 hover:shadow-md transition-shadow"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="font-display text-xl font-bold text-gray-900">
                  {feature.title}
                </h3>
                <p className="mt-2 font-body text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ─── Social Proof Stats ──────────────────────────────────────────────────────

function SocialProofStats() {
  const stats = [
    { value: '30 seconds', description: 'to generate year-end tax reports' },
    { value: '3 loan types', description: 'standard, interest-only, balloon' },
    { value: '3 export formats', description: 'PDF, CSV, QuickBooks' },
  ]

  return (
    <section className="bg-gray-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {stats.map((stat) => (
              <div
                key={stat.value}
                className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 text-center"
              >
                <p className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
                  {stat.value}
                </p>
                <p className="mt-2 font-body text-gray-500">{stat.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ─── Related Pages ───────────────────────────────────────────────────────────

function RelatedPages() {
  const pages = [
    {
      href: '/stop-using-spreadsheets-loan-tracking',
      title: 'Stop Using Spreadsheets',
      description: 'Your lending spreadsheet was fine. Until it wasn\u2019t.',
    },
    {
      href: '/manage-multiple-private-loans',
      title: 'Manage Multiple Loans',
      description: 'Two loans was easy. Five is where things break.',
    },
  ]

  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            You might also find these helpful
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {pages.map((page) => (
              <Link
                key={page.href}
                href={page.href}
                className="block rounded-2xl border border-gray-200 p-6 sm:p-8 hover:shadow-md hover:border-emerald-200 transition-all"
              >
                <h3 className="font-display text-lg font-bold text-gray-900">
                  {page.title}
                </h3>
                <p className="mt-2 font-body text-gray-600">{page.description}</p>
                <span className="mt-4 inline-block text-sm font-semibold text-emerald-600">
                  Read more &rarr;
                </span>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ─── FAQ Section ─────────────────────────────────────────────────────────────

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-gray-50 py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            Frequently Asked Questions
          </h2>

          <div className="mt-10 space-y-4">
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = openIndex === index
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                  >
                    <span className="font-display text-base font-semibold text-gray-900 pr-4">
                      {item.question}
                    </span>
                    <svg
                      className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-5 font-body text-gray-600 leading-relaxed">
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ─── Final CTA ───────────────────────────────────────────────────────────────

function FinalCta() {
  return (
    <section
      style={{ background: 'linear-gradient(165deg, #0E4D30 0%, #081C12 100%)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <motion.div {...fadeInUp}>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
            Stop scrambling at tax time.
          </h2>
          <p className="mt-4 text-lg text-gray-300 font-body max-w-xl mx-auto">
            LendSolo tracks every dollar of interest so you don&rsquo;t have to.
          </p>
          <Link
            href="/users/sign_up"
            className="mt-8 inline-flex items-center px-8 py-3.5 rounded-lg text-base font-semibold text-gray-900 transition-colors hover:brightness-110"
            style={{ backgroundColor: '#34D399' }}
          >
            Start Free &rarr;
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

